import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {SlideDirection} from './SlideDirection.enum';

interface Props {
  lastIndex: number;
  isInfinite: boolean;
  setCurrentIndex: Dispatch<SetStateAction<number>>;
  slideDirectionRef: MutableRefObject<SlideDirection>;
  autoInterval: number;
}
export function useCarouselMethods({
  lastIndex,
  isInfinite,
  slideDirectionRef,
  setCurrentIndex,
  autoInterval,
}: Props) {
  const goToNext = useCallback(() => {
    slideDirectionRef.current = SlideDirection.Right;
    setCurrentIndex(prevIndex => {
      if (prevIndex === lastIndex) {
        if (isInfinite) {
          return 0;
        } else {
          return prevIndex;
        }
      }
      return prevIndex + 1;
    });
  }, [isInfinite, lastIndex, slideDirectionRef, setCurrentIndex]);

  const goToPrev = useCallback(() => {
    slideDirectionRef.current = SlideDirection.Left;
    setCurrentIndex(prevIndex => {
      if (prevIndex === 0) {
        if (isInfinite) {
          return lastIndex;
        } else {
          return prevIndex;
        }
      }
      return prevIndex - 1;
    });
  }, [isInfinite, lastIndex, slideDirectionRef, setCurrentIndex]);

  const goToIndex = useCallback(
    (index: number) => {
      slideDirectionRef.current = SlideDirection.None;
      if (index < 0 || index > lastIndex) {
        return;
      }
      setCurrentIndex(Math.min(lastIndex, index));
    },
    [lastIndex, setCurrentIndex, slideDirectionRef]
  );

  const autoPlayTimerRef = useRef(0);

  const autoPlayCarousel = useCallback(() => {
    autoPlayTimerRef.current = window.setTimeout(
      autoPlayCarousel,
      autoInterval
    );
    goToNext();
  }, [goToNext, autoInterval]);

  const stopAutoplay = useCallback(() => {
    window.clearTimeout(autoPlayTimerRef.current);
  }, []);

  const startAutoplay = useCallback(() => {
    stopAutoplay();
    autoPlayTimerRef.current = window.setTimeout(
      autoPlayCarousel,
      autoInterval
    );
  }, [autoInterval, autoPlayCarousel, stopAutoplay]);

  useEffect(() => {
    return () => stopAutoplay();
  }, [stopAutoplay]);

  return {
    goToNext,
    goToPrev,
    goToIndex,
    stopAutoplay,
    startAutoplay,
  };
}
